import { useId, useLayoutEffect, useRef, useState } from 'react';
import { formatTimeAgo, STATUS_LABELS } from '../utils';
import { StatusDot } from './Status';

function windowPulse(points, days) {
  if (days === 'all') return points;
  const count = Number(days);
  return points.slice(-count);
}

function smoothCounts(points) {
  return points.map((point, index) => {
    const start = Math.max(0, index - 6);
    const window = points.slice(start, index + 1);
    return { ...point, average: window.reduce((sum, item) => sum + item.count, 0) / window.length };
  });
}

function makePath(points, width, height, key, scaleMax) {
  if (points.length === 0) return '';
  const max = scaleMax ?? Math.max(1, ...points.map((point) => point[key]));
  const coordinates = points.map((point, index) => {
    const x = points.length === 1 ? width / 2 : (index / (points.length - 1)) * width;
    const y = height - (point[key] / max) * (height - 12) - 6;
    return [x, y];
  });
  if (coordinates.length === 1) return `M${coordinates[0][0]},${coordinates[0][1]}`;
  return coordinates.reduce((path, point, index) => {
    if (index === 0) return `M${point[0].toFixed(2)},${point[1].toFixed(2)}`;
    const previous = coordinates[index - 1];
    const midX = (previous[0] + point[0]) / 2;
    return `${path} C${midX.toFixed(2)},${previous[1].toFixed(2)} ${midX.toFixed(2)},${point[1].toFixed(2)} ${point[0].toFixed(2)},${point[1].toFixed(2)}`;
  }, '');
}

export function MemoryPulse({ points, range, onRange }) {
  const gradientId = useId().replace(/:/g, '');
  const [hoverIndex, setHoverIndex] = useState(null);
  const chartRef = useRef(null);
  const [chartSize, setChartSize] = useState({ width: 720, height: 230 });
  useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) return undefined;
    const update = () => {
      const rect = chart.getBoundingClientRect();
      setChartSize({ width: Math.max(1, rect.width), height: Math.max(1, rect.height - 18) });
    };
    update(); const observer = new ResizeObserver(update); observer.observe(chart); return () => observer.disconnect();
  }, []);
  const visible = smoothCounts(windowPulse(points, range));
  const { width, height } = chartSize;
  const max = Math.max(1, ...visible.map((point) => point.count));
  const line = makePath(visible, width, height, 'count', max);
  const average = makePath(visible, width, height, 'average', max);
  const area = line ? `${line} L${width},${height} L0,${height} Z` : '';
  const peakIndex = visible.findIndex((point) => point.count === max);
  const activeIndex = hoverIndex ?? peakIndex;
  const activePoint = visible[activeIndex];
  const activeX = visible.length <= 1 ? width / 2 : (activeIndex / (visible.length - 1)) * width;
  const activeY = activePoint ? height - (activePoint.count / max) * (height - 12) - 6 : height;
  const tooltipX = Math.max(4, Math.min(width - 128, activeX - 64));
  const tooltipY = activeY < 55 ? activeY + 14 : activeY - 50;
  const labels = visible.length ? [visible[0], visible[Math.floor(visible.length / 2)], visible.at(-1)] : [];
  const hoverDate = activePoint ? new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${activePoint.date}T00:00:00Z`)) : '';
  const handlePointerMove = (event) => {
    if (!visible.length) return;
    const svg = event.currentTarget;
    const matrix = svg.getScreenCTM();
    if (!matrix) return;
    const pointer = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
    setHoverIndex(Math.max(0, Math.min(visible.length - 1, Math.round((pointer.x / width) * (visible.length - 1)))));
  };

  return (
    <section className="pulse-panel">
      <div className="section-heading pulse-panel__heading">
        <div><h2>Memory pulse</h2><p><span className="legend-line" /> Activity <span className="legend-dash" /> 7-day average</p></div>
        <div className="range-control" aria-label="Chart date range">
          {[['7', '7D'], ['30', '30D'], ['90', '90D'], ['180', '6M'], ['365', '1Y'], ['all', 'ALL']].map(([value, label]) => (
            <button key={value} className={range === value ? 'is-active' : ''} onClick={() => onRange(value)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="pulse-chart" ref={chartRef}>
        <div className="pulse-chart__grid" aria-hidden="true"><i/><i/><i/><i/></div>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Memory activity across ${visible.length} days`} preserveAspectRatio="xMidYMid meet" onPointerMove={handlePointerMove} onPointerLeave={() => setHoverIndex(null)}>
          <defs><linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1"><stop stopColor="#ff6b5d" stopOpacity=".5"/><stop offset="1" stopColor="#ff6b5d" stopOpacity="0"/></linearGradient></defs>
          <path d={area} fill={`url(#${gradientId})`} />
          <path d={average} className="chart-average" />
          <path d={line} className="chart-line" pathLength="1" />
          <rect className="chart-hit-area" width={width} height={height}/>
          {activePoint ? <g className="chart-active-marker"><line x1={activeX} x2={activeX} y1={activeY} y2={height} className="chart-peak-line"/><circle cx={activeX} cy={activeY} r="5" className="chart-peak-dot" />{hoverIndex !== null ? <g className="chart-tooltip" transform={`translate(${tooltipX} ${tooltipY})`} data-hover-date={activePoint.date} data-hover-count={activePoint.count}><rect width="128" height="40" rx="4"/><text x="9" y="15">{hoverDate}</text><text className="chart-tooltip__count" x="9" y="30">{activePoint.count} {activePoint.count === 1 ? 'memory' : 'memories'}</text></g> : null}</g> : null}
        </svg>
        <div className="pulse-chart__labels">
          {labels.map((point) => <span key={point.date}>{new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(`${point.date}T00:00:00Z`))}</span>)}
        </div>
      </div>
    </section>
  );
}

export function EvidenceQuality({ counts, onExplore }) {
  const entries = ['success', 'partial', 'uncertain', 'current', 'planned']
    .map((status) => [status, counts[status] ?? 0])
    .filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0) || 1;
  let cursor = 0;
  const stops = entries.map(([status, count]) => {
    const start = cursor;
    cursor += (count / total) * 100;
    return `var(--status-${status}) ${start}% ${cursor}%`;
  });
  return (
    <section className="evidence-panel">
      <div className="section-heading"><div><h2>Evidence quality</h2><p>Task outcomes in the registry</p></div></div>
      <div className="evidence-panel__visual">
        <div className="donut" style={{ background: `conic-gradient(${stops.join(',')})` }}>
          <div><strong>{total}</strong><span>Tasks</span></div>
        </div>
        <div className="evidence-legend">
          {entries.map(([status, count]) => (
            <div key={status}><StatusDot status={status} /><strong>{Math.round((count / total) * 100)}%</strong><span>{count}</span></div>
          ))}
        </div>
      </div>
      <button className="text-action" onClick={onExplore}>View all tasks <span>→</span></button>
    </section>
  );
}

export function EvidenceRail({ counts, onExplore }) {
  const entries = ['success', 'partial', 'uncertain', 'current', 'planned']
    .map((status) => [status, counts[status] ?? 0]).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, count]) => sum + count, 0) || 1;
  return (
    <section className="evidence-rail">
      <div className="section-heading"><div><h2>Evidence</h2><p>Registry outcomes</p></div><button className="text-action" onClick={onExplore}>Inspect →</button></div>
      <div className="evidence-rail__bar">{entries.map(([status,count]) => <span key={status} className={`is-${status}`} style={{width:`${count/total*100}%`}} title={`${STATUS_LABELS[status]} ${count}`}/>)}</div>
      <div className="evidence-rail__legend">{entries.map(([status,count]) => <div key={status}><StatusDot status={status}/><strong>{Math.round(count/total*100)}%</strong></div>)}</div>
    </section>
  );
}

export function SourceFreshness({ sources }) {
  return (
    <section className="source-freshness">
      <div className="section-heading"><div><h2>Source freshness</h2><p>Latest change by source</p></div></div>
      <div>{sources.slice(0,6).map((source) => <p key={source.category}><span>{source.category}</span><time>{formatTimeAgo(source.updatedAt)}</time></p>)}</div>
    </section>
  );
}

export function TopicConstellation({ topics, onTopic }) {
  const palette = ['#ff6b5d', '#83d6af', '#f2b84b', '#9aa9b1', '#efe9dc', '#d88479', '#769d92', '#c9a871'];
  const points = [
    [49,45],[28,24],[70,23],[22,61],[76,63],[49,78],[15,39],[86,42],
  ];
  const max = Math.max(...topics.map((topic) => topic.count), 1);
  return (
    <section className="topics-panel">
      <div className="section-heading"><div><h2>Topic constellation</h2><p>Where your memory clusters</p></div></div>
      <div className="constellation">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          {points.slice(0, topics.length).flatMap((point, index) => points.slice(index + 1, topics.length).filter((_, connection) => (connection + index) % 3 === 0).map((target, connection) => <line key={`${index}-${connection}`} x1={point[0]} y1={point[1]} x2={target[0]} y2={target[1]} />))}
          {topics.slice(0, 8).map((topic, index) => <circle key={topic.name} cx={points[index][0]} cy={points[index][1]} r={2.3 + (topic.count / max) * 3.8} fill={palette[index]} />)}
        </svg>
        {topics.slice(0, 8).map((topic, index) => (
          <button key={topic.name} style={{ left: `${points[index][0]}%`, top: `${points[index][1]}%`, '--topic': palette[index] }} onClick={() => onTopic(topic.name)}>
            <span>{topic.name}</span><small>{topic.count}</small>
          </button>
        ))}
      </div>
      <button className="text-action" onClick={() => onTopic('')}>Explore knowledge <span>→</span></button>
    </section>
  );
}

export function TimelineBars({ points }) {
  const months = new Map();
  points.forEach((point) => {
    const month = point.date.slice(0, 7);
    months.set(month, (months.get(month) ?? 0) + point.count);
  });
  const values = [...months.entries()].slice(-12);
  const max = Math.max(...values.map(([, count]) => count), 1);
  return (
    <div className="timeline-bars" aria-label="Rollout activity by month">
      {values.map(([month, count]) => (
        <div key={month} title={`${month}: ${count} rollouts`}>
          <span style={{ height: `${Math.max(5, count / max * 100)}%` }} />
          <small>{new Intl.DateTimeFormat('en', { month: 'short' }).format(new Date(`${month}-01T00:00:00Z`))}</small>
        </div>
      ))}
    </div>
  );
}
